import { Search, SlidersHorizontal, X } from 'lucide-react'
import { useId, useState } from 'react'

import type { Branch, CustomerStatus, KycStatus } from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

import { CUSTOMER_STATUSES, KYC_STATUSES, customerStatusLabel, kycStatusLabel } from './constants'
import { ROW_NAV_SOURCE_PROPS } from './use-row-navigation'

/**
 * The customer list toolbar.
 *
 * `ANY` stands in for "no filter" because a select option cannot carry an empty value, and every
 * filter is a single choice rather than a multi-select: the list is a working queue, and a teller
 * narrowing it needs one decision per control, not a set to assemble.
 *
 * On a narrow screen the search box stays and the three narrowing controls fold behind a
 * disclosure. Someone reaching for this from a phone has a customer on the telephone and a name to
 * type; four stacked controls would put the results below the fold before the search had run.
 */
const ANY = 'any'

export interface CustomerFiltersProps {
  searchText: string
  onSearchTextChange: (value: string) => void
  status: CustomerStatus | undefined
  onStatusChange: (status: CustomerStatus | undefined) => void
  kycStatus: KycStatus | undefined
  onKycStatusChange: (kycStatus: KycStatus | undefined) => void
  branchId: string | undefined
  onBranchChange: (branchId: string | undefined) => void
  branches: Branch[]
  hasActiveFilters: boolean
  onClearFilters: () => void
}

export function CustomerFilters({
  searchText,
  onSearchTextChange,
  status,
  onStatusChange,
  kycStatus,
  onKycStatusChange,
  branchId,
  onBranchChange,
  branches,
  hasActiveFilters,
  onClearFilters,
}: CustomerFiltersProps) {
  const searchId = useId()
  const statusId = useId()
  const kycId = useId()
  const branchFilterId = useId()
  const narrowingId = useId()

  const compact = !useIsAtLeast('sm')
  const narrowingCount = [status, kycStatus, branchId].filter(Boolean).length
  const [narrowingOpen, setNarrowingOpen] = useState(narrowingCount > 0)

  return (
    <div className="space-y-1.5 rounded-card border border-border-subtle bg-surface p-3">
      <div className="grid gap-3 sm:flex sm:flex-wrap sm:items-end">
        <div className="min-w-0 space-y-1.5 sm:min-w-64 sm:flex-1">
          <Label htmlFor={searchId}>Search</Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <Input
              {...ROW_NAV_SOURCE_PROPS}
              id={searchId}
              value={searchText}
              // The screen opens with the caret here. A teller with a customer at the counter
              // starts typing a name before the page has finished settling.
              //
              // Not on a phone: the same focus raises the on-screen keyboard over the results the
              // operator came to read, before they have asked for anything.
              autoFocus={!compact}
              autoComplete="off"
              spellCheck={false}
              type="search"
              placeholder="Name, customer number, or the full phone number"
              className="pr-8 pl-8"
              onChange={(event) => onSearchTextChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && searchText !== '') {
                  event.preventDefault()
                  onSearchTextChange('')
                }
              }}
            />
            {searchText ? (
              <button
                type="button"
                onClick={() => onSearchTextChange('')}
                className="absolute top-1/2 right-1.5 flex size-6 -translate-y-1/2 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface-hover hover:text-heading max-sm:size-11"
                aria-label="Clear the search"
                tabIndex={-1}
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        {compact ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              aria-expanded={narrowingOpen}
              aria-controls={narrowingId}
              onClick={() => setNarrowingOpen((open) => !open)}
            >
              <SlidersHorizontal />
              Filters
              {narrowingCount > 0 ? <Badge tone="brand">{narrowingCount}</Badge> : null}
            </Button>
            {hasActiveFilters ? (
              <Button variant="ghost" onClick={onClearFilters}>
                <X />
                Clear
              </Button>
            ) : null}
          </div>
        ) : null}

        <div
          id={narrowingId}
          className={cn(
            'grid grid-cols-2 gap-3 sm:contents',
            compact && !narrowingOpen && 'hidden',
          )}
        >
          <div className="space-y-1.5 sm:w-40">
            <Label htmlFor={statusId}>Status</Label>
            <Select
              value={status ?? ANY}
              onValueChange={(value) => onStatusChange(value === ANY ? undefined : (value as CustomerStatus))}
            >
              <SelectTrigger id={statusId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any status</SelectItem>
                {CUSTOMER_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {customerStatusLabel(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:w-44">
            <Label htmlFor={kycId}>KYC</Label>
            <Select
              value={kycStatus ?? ANY}
              onValueChange={(value) => onKycStatusChange(value === ANY ? undefined : (value as KycStatus))}
            >
              <SelectTrigger id={kycId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any KYC state</SelectItem>
                {KYC_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {kycStatusLabel(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-1.5 sm:w-56">
            <Label htmlFor={branchFilterId}>Branch</Label>
            <Select
              value={branchId ?? ANY}
              onValueChange={(value) => onBranchChange(value === ANY ? undefined : value)}
            >
              <SelectTrigger id={branchFilterId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Every branch I can see</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!compact && hasActiveFilters ? (
          <Button variant="ghost" onClick={onClearFilters}>
            <X />
            Clear
          </Button>
        ) : null}
      </div>

      <p className="text-2xs text-muted">
        Names and customer numbers match on any part. A phone number matches only in full — the
        column is encrypted, so there is no "last four digits" search to offer.
      </p>
    </div>
  )
}
